/**
 * Shared DistrictSelect component.
 * Renders a <select> with all Korean regions & districts,
 * used in AdminPage, InquiryPage, etc.
 */
import React, { useState, useEffect } from "react";
import KOREA_REGIONS, {
  getDistrictRegions,
  getQualifiedDistrictLabel,
  getRegionData,
  normalizeRegionKey,
  splitQualifiedDistrict,
} from "../data/koreaDistricts";

function isAmbiguousDistrict(districtName) {
  return getDistrictRegions(districtName).length > 1;
}

function districtOptionValue(regionShort, districtName) {
  return isAmbiguousDistrict(districtName)
    ? getQualifiedDistrictLabel(regionShort, districtName)
    : districtName;
}

function districtOptionLabel(regionShort, districtName) {
  return isAmbiguousDistrict(districtName)
    ? getQualifiedDistrictLabel(regionShort, districtName)
    : districtName;
}

export default function DistrictSelect({ value, onChange, className, placeholder }) {
  const hasMatchingOption = KOREA_REGIONS.some((r) => {
    if (value === r.short || value === r.region || value === "전국") return true;
    return r.districts.some((d) => value === districtOptionValue(r.short, d.name));
  });

  return (
    <select value={value || ""} onChange={onChange} className={className}>
      <option value="">{placeholder || "-- 지역 선택 --"}</option>
      {value && !hasMatchingOption && <option value={value}>{value}</option>}
      <option value="전국">🌏 전국</option>
      {KOREA_REGIONS.map((r) => (
        <optgroup key={r.short} label={r.region}>
          <option value={r.short}>🏙️ {r.short} (전체)</option>
          {r.districts.map((d) => (
            <option key={`${r.short}-${d.name}`} value={districtOptionValue(r.short, d.name)}>
              &nbsp;&nbsp;📍 {districtOptionLabel(r.short, d.name)}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/** 2-level cascading district selector: 시/도 → 구/군 */
export function DistrictLevelSelect({ value, onChange, className }) {
  const detectRegion = (val) => {
    if (!val) return "";
    const parsed = splitQualifiedDistrict(val);
    if (parsed.region) return parsed.region;
    const r = getRegionData(val);
    if (r) return r.short;
    const matches = getDistrictRegions(parsed.district || val);
    if (matches.length === 1) return matches[0].short;
    return "";
  };

  const [region, setRegion] = useState(() => detectRegion(value));

  useEffect(() => {
    setRegion(detectRegion(value));
  }, [value]);

  const fire = (val) => onChange({ target: { value: val } });

  const handleRegion = (rg) => {
    setRegion(rg);
    if (rg) fire(rg);
    else fire("");
  };

  const handleDistrict = (d) => fire(d ? districtOptionValue(region, d) : "");

  const regionData = KOREA_REGIONS.find((r) => r.short === region);
  const selectedDistrict = splitQualifiedDistrict(value).district || value || "";

  const selectBase = `${className || ""} w-full text-sm appearance-none bg-white`;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <select value={region} onChange={(e) => handleRegion(e.target.value)} className={selectBase}>
          <option value="">-- 시/도 선택 --</option>
          {KOREA_REGIONS.map((r) => (
            <option key={r.short} value={r.short}>{r.region}</option>
          ))}
        </select>
        {region && regionData && (
          <select
            value={regionData.districts.some((d) => d.name === selectedDistrict) ? selectedDistrict : ""}
            onChange={(e) => handleDistrict(e.target.value)}
            className={selectBase}
          >
            <option value="">-- 구/군 선택 --</option>
            {regionData.districts.map((d) => (
              <option key={d.name} value={d.name}>{d.name}</option>
            ))}
          </select>
        )}
      </div>
      {value && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center text-xs text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg font-medium">
            {KOREA_REGIONS.some((r) => r.short === normalizeRegionKey(value)) ? `🏙️ ${normalizeRegionKey(value)} 전체` : `📍 ${getQualifiedDistrictLabel(region, value)}`}
          </span>
          <button type="button" onClick={() => { setRegion(""); fire(""); }}
            className="text-xs text-gray-400 hover:text-red-400 transition">✕ 초기화</button>
        </div>
      )}
    </div>
  );
}

/** Flat array of all district names for quick iteration */
export function getAllDistrictNames() {
  const names = [];
  KOREA_REGIONS.forEach((r) => {
    r.districts.forEach((d) => names.push(d.name));
  });
  return names;
}
